import { Injectable, inject } from '@angular/core';
import { Observable } from 'rxjs';
import { map, shareReplay } from 'rxjs/operators';
import { API_ENDPOINTS } from '../constants/api-endpoints';
import { ApiResponse } from '../models/api-response.models';
import { CreateRoleRequest, Role } from '../models/user.models';
import { unwrapApiResponse } from '../utils/api-response';
import { ApiService } from './api.service';

@Injectable({
  providedIn: 'root',
})
export class RolesService {
  private readonly api = inject(ApiService);
  private readonly rolesCache = new Map<string, Observable<Role[]>>();

  list(tenantId?: string | null): Observable<Role[]> {
    const key = tenantId || 'current';
    const cached = this.rolesCache.get(key);
    if (cached) return cached;
    const request$ = this.api.get<ApiResponse<Role[]> | Role[]>(API_ENDPOINTS.roles, tenantId ? { tenantId } : undefined).pipe(
      map((response) => unwrapApiResponse(response)),
      map((response) =>
        Array.isArray(response)
          ? response
          : ((response as unknown as { roles?: Role[] }).roles ?? []),
      ), shareReplay({ bufferSize: 1, refCount: false }),
    );
    this.rolesCache.set(key, request$);
    return request$;
  }


  create(payload: CreateRoleRequest): Observable<Role> {
    this.rolesCache.delete(payload.tenantId || 'current');
    return this.api
      .post<ApiResponse<Role | { role: Role }> | Role | { role: Role }, CreateRoleRequest>(API_ENDPOINTS.roles, payload)
      .pipe(
        map((response) => unwrapApiResponse(response)),
        map((response) => {
          const role = 'role' in response ? response.role : response;
          return { ...role, users: role.users ?? 0, permissions: role.permissions ?? [] };
        }),
      );
  }

  assignPermission(roleId: string, permissionId: string): Observable<void> {
    return this.api
      .post<ApiResponse<void> | void, null>(
        `${API_ENDPOINTS.roles}/${roleId}/permissions/${permissionId}`,
        null,
      )
      .pipe(map((response) => unwrapApiResponse(response)));
  }

  permissionIds(roleId: string): Observable<string[]> {
    return this.api
      .get<ApiResponse<{ permissionIds?: string[] }> | { permissionIds?: string[] }>(
        `${API_ENDPOINTS.roles}/${roleId}/permissions`,
      )
      .pipe(
        map((response) => unwrapApiResponse(response)),
        map((response) => response.permissionIds ?? []),
      );
  }

  replacePermissions(roleId: string, permissionIds: string[]): Observable<string[]> {
    return this.api
      .put<
        ApiResponse<{ permissionIds?: string[] }> | { permissionIds?: string[] },
        { permissionIds: string[] }
      >(`${API_ENDPOINTS.roles}/${roleId}/permissions`, { permissionIds })
      .pipe(
        map((response) => unwrapApiResponse(response)),
        map((response) => response.permissionIds ?? permissionIds),
      );
  }
}
